import React, { useState, useEffect, useCallback, useRef } from "react";
import BaseModal from "../shared/BaseModal";
import Button from "../shared/Button";
import {
  getStatusLabel,
  getStatusColor,
  getMealPreferenceLabel,
  getResponseOptions,
  getMealPreferenceOptions,
} from "../../types/registration.types";
import {
  getCheckInQRUrl,
  updateOwnRegistration,
  getEventForRegistration,
} from "../../hooks/useRegistration";
import { downloadQRCode } from "../utils/qrUtils";
import shortfyLogoPng from "../../assets/images/Shortfy-Logo.png";
import "./MyRegistrationCard.css";
import BaseCard from "../shared/cards/BaseCard";
import CardHeader from "../shared/cards/CardHeader";
import CardContent from "../shared/cards/CardContent";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import { useNotification } from "../../context/NotificationContext";
import { EventRegistration, MealPreference, RegistrationStatus } from "../../types/shared.types";

interface EventInfo {
  eventName: string;
  eventStartDate: string;
  eventEndDate: string;
  location: string;
  mealOptionsEnabled: boolean;
  additionalGuestsEnabled: boolean;
}

interface MyRegistrationCardProps {
  registration: EventRegistration;
  onUpdateSuccess: (registration: EventRegistration) => void;
  onCancelSuccess: (registrationId: string) => void;
  showQRCode?: boolean;
  highlightAction?: boolean;
  isReadOnly?: boolean;
}

const MyRegistrationCard: React.FC<MyRegistrationCardProps> = ({
  registration,
  onUpdateSuccess,
  onCancelSuccess,
  isReadOnly = false,
}) => {
  const [showChangeModal, setShowChangeModal] = useState(false);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [isLoadingEvent, setIsLoadingEvent] = useState(true);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  const [newStatus, setNewStatus] = useState(registration.status);
  const [newMealPreference, setNewMealPreference] = useState(
    registration.mealPreference,
  );
  const [newAdditionalAdults, setNewAdditionalAdults] = useState(registration.additionalAdults ?? 0);
  const [newAdditionalChildren, setNewAdditionalChildren] = useState(registration.additionalChildren ?? 0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { setNotification } = useNotification();

  const checkInUrl = registration.checkInCode
    ? getCheckInQRUrl(registration.checkInCode)
    : "";
  const responseOptions = getResponseOptions();
  const mealOptions = getMealPreferenceOptions();

  const fetchEventInfo = useCallback(async () => {
    setIsLoadingEvent(true);
    const response = await getEventForRegistration(registration.eventId);
    if (response.success && response.data) {
      setEventInfo({
        eventName: response.data.eventName,
        eventStartDate: response.data.eventStartDate,
        eventEndDate: response.data.eventEndDate,
        location: response.data.location,
        mealOptionsEnabled: response.data.mealOptionsEnabled,
        additionalGuestsEnabled: response.data.additionalGuestsEnabled,
      });
    }
    setIsLoadingEvent(false);
  }, [registration.eventId]);

  useEffect(() => {
    fetchEventInfo();
  }, [fetchEventInfo]);

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        downloadRef.current &&
        !downloadRef.current.contains(e.target as Node)
      ) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [downloadMenuOpen]);

  const formatEventDate = (isoDate: string): string =>
    new Date(isoDate).toLocaleDateString("de-DE", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const isEventPast = () => {
    if (!eventInfo) return false;
    return new Date(eventInfo.eventEndDate) < new Date();
  };

  const canChangeResponse = () => {
    if (isReadOnly || isEventPast()) return false;
    return (
      registration.status !== RegistrationStatus.Attended &&
      registration.status !== RegistrationStatus.Cancelled
    );
  };

  const handleDownloadQR = async (format: "png" | "svg") => {
    const eventName = eventInfo?.eventName || "event";
    const fileName = `${eventName.replace(/[^a-z0-9]/gi, "_")}_checkin_qr`;

    await downloadQRCode({
      fileName,
      format,
      qrCodeElementId: `reg-qr-${registration.registrationId}`,
      logoUrl: "/images/CGI-Logo.jpg",
      withLogo: true,
      includeDisclaimer: true,
      shortfyLogoUrl: shortfyLogoPng,
    });
  };

  const openChangeModal = () => {
    setNewStatus(registration.status);
    setNewMealPreference(registration.mealPreference);
    setNewAdditionalAdults(registration.additionalAdults ?? 0);
    setNewAdditionalChildren(registration.additionalChildren ?? 0);
    setShowChangeModal(true);
  };

  const handleChangeSubmit = async () => {
    setIsSubmitting(true);

    const isRegistered = newStatus === RegistrationStatus.Registered;
    const response = await updateOwnRegistration(
      registration.eventId,
      registration.userEmail,
      {
        status: newStatus,
        mealPreference: newMealPreference,
        ...(eventInfo?.additionalGuestsEnabled && isRegistered
          ? { additionalAdults: newAdditionalAdults, additionalChildren: newAdditionalChildren }
          : { additionalAdults: 0, additionalChildren: 0 }),
      },
    );

    if (response.success && response.data) {
      onUpdateSuccess(response.data);
      setShowChangeModal(false);
    } else {
      setNotification({
        type: "error",
        message: response.error || "Failed to update registration",
      });
    }
    setIsSubmitting(false);
  };

  const renderQRSection = () => {
    if (
      registration.status === RegistrationStatus.Registered &&
      registration.checkInCode
    ) {
      return (
        <div
          className="card-qr-image"
          id={`reg-qr-${registration.registrationId}`}
        >
          <QRCodeRenderer
            value={checkInUrl}
            size={200}
            withLogo
            logoUrl="/images/CGI-Logo.jpg"
          />
        </div>
      );
    }

    return (
      <div className="reg-card-qr-placeholder">
        <div className="reg-card-qr-icon">📋</div>
        <div className="reg-card-qr-text">
          {registration.status === RegistrationStatus.Tentative &&
            "Confirm to get QR code"}
          {registration.status === RegistrationStatus.Declined &&
            "Registration declined"}
          {registration.status === RegistrationStatus.Cancelled &&
            "Registration cancelled"}
          {registration.status === RegistrationStatus.Attended &&
            "Event completed"}
        </div>
      </div>
    );
  };

  const renderInfoSection = () => (
    <>
      <div className="card-info-lines">
        {eventInfo && (
          <>
            <div>
              <span className="card-label">Registered Name:</span>
              <span className="card-value">{registration.userName}</span>
            </div>
            {registration.employeeId && (
              <div>
                <span className="card-label">Employee ID:</span>
                <span className="card-value">{registration.employeeId}</span>
              </div>
            )}
            <div>
              <span className="card-label">Event Date:</span>
              <span className="card-value">
                {formatEventDate(eventInfo.eventStartDate)} –{" "}
                {formatEventDate(eventInfo.eventEndDate)}
              </span>
            </div>
            <div>
              <span className="card-label">Location:</span>
              <span className="card-value">{eventInfo.location}</span>
            </div>
          </>
        )}

        {registration.mealPreference &&
          registration.mealPreference !== MealPreference.None && (
            <div>
              <span className="card-label">Meal Preference:</span>
              <span className="card-value">
                {getMealPreferenceLabel(registration.mealPreference)}
              </span>
            </div>
          )}

        {eventInfo?.additionalGuestsEnabled &&
          registration.status === RegistrationStatus.Registered &&
          ((registration.additionalAdults ?? 0) > 0 ||
            (registration.additionalChildren ?? 0) > 0) && (
            <div>
              <span className="card-label">Additional Guests:</span>
              <span className="card-value">
                {[
                  (registration.additionalAdults ?? 0) > 0 &&
                    `${registration.additionalAdults} adult${registration.additionalAdults !== 1 ? "s" : ""}`,
                  (registration.additionalChildren ?? 0) > 0 &&
                    `${registration.additionalChildren} child${registration.additionalChildren !== 1 ? "ren" : ""}`,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </span>
            </div>
          )}

        {registration.notes && (
          <div>
            <span className="card-label">Notes:</span>
            <span className="card-value">{registration.notes}</span>
          </div>
        )}

        {registration.checkInCode &&
          registration.status === RegistrationStatus.Registered && (
            <div>
              <span className="card-label">Check-in Code:</span>
              <span className="card-value reg-card-checkin-code">
                {registration.checkInCode}
              </span>
            </div>
          )}
      </div>

      {isEventPast() && (
        <div className="reg-card-past-notice">
          This event has already taken place
        </div>
      )}

      <div className="card-actions">
        {registration.status === RegistrationStatus.Registered &&
          registration.checkInCode && (
            <div className="card-download-wrapper" ref={downloadRef}>
              <Button
                type="button"
                variant="secondary"
                className="card-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setDownloadMenuOpen((o) => !o);
                }}
              >
                Download QR
              </Button>

              {downloadMenuOpen && (
                <div className="card-download-dropdown">
                  <button
                    className="card-download-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadQR("png");
                      setDownloadMenuOpen(false);
                    }}
                  >
                    PNG
                  </button>
                  <button
                    className="card-download-option"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownloadQR("svg");
                      setDownloadMenuOpen(false);
                    }}
                  >
                    SVG
                  </button>
                </div>
              )}
            </div>
          )}

        {canChangeResponse() && (
          <Button
            type="button"
            variant="primary"
            className="card-btn"
            onClick={(e) => {
              e.stopPropagation();
              openChangeModal();
            }}
          >
            Change Response
          </Button>
        )}
      </div>
    </>
  );

  return (
    <>
      <BaseCard
        id={registration.registrationId}
        renderHeader={({ isExpanded, onToggle }) => (
          <CardHeader
            title={eventInfo?.eventName || "Unknown Event"}
            isExpanded={isExpanded}
            onToggle={onToggle}
            isLoading={isLoadingEvent}
            statusBadge={
              <span
                className="card-status-badge"
                style={{ backgroundColor: getStatusColor(registration.status) }}
              >
                {getStatusLabel(registration.status)}
              </span>
            }
          />
        )}
      >
        <CardContent
          qrSection={renderQRSection()}
          infoSection={renderInfoSection()}
        />
      </BaseCard>

      <BaseModal
        isOpen={showChangeModal}
        onClose={() => setShowChangeModal(false)}
        title="Change Your Response"
        showCloseButton
      >
        <div className="reg-card-modal">
          <p className="reg-card-modal-subtitle">
            Update your response for <strong>{eventInfo?.eventName}</strong>
          </p>

          <div
            className="reg-card-modal-options"
            role="radiogroup"
            aria-label="Response options"
          >
            {responseOptions.map((option) => {
              const selected = newStatus === option.value;
              return (
                <Button
                  key={option.value}
                  type="button"
                  variant={selected ? "primary" : "outline"}
                  className={`reg-card-modal-option ${selected ? "selected" : ""}`}
                  style={{ borderColor: option.color }}
                  onClick={() => {
                    setNewStatus(option.value);
                    if (option.value !== RegistrationStatus.Registered) {
                      setNewMealPreference(MealPreference.None);
                      setNewAdditionalAdults(0);
                      setNewAdditionalChildren(0);
                    }
                  }}
                  disabled={isSubmitting}
                  role="radio"
                  aria-checked={selected}
                >
                  <span
                    className="reg-card-modal-option-icon"
                    aria-hidden="true"
                  >
                    {option.value === RegistrationStatus.Registered && "✓"}
                    {option.value === RegistrationStatus.Tentative && "?"}
                    {option.value === RegistrationStatus.Declined && "✗"}
                  </span>
                  <span className="reg-card-modal-option-label">
                    {option.label}
                  </span>
                </Button>
              );
            })}
          </div>

          {eventInfo?.additionalGuestsEnabled &&
            newStatus === RegistrationStatus.Registered && (
              <div className="reg-card-modal-meals">
                <h4>Additional Guests</h4>
                <div className="reg-card-modal-guest-spinners">
                  <div className="reg-card-modal-spinner-group">
                    <label>Additional Adults</label>
                    <div className="spinner-controls">
                      <button type="button" className="spinner-btn" onClick={() => setNewAdditionalAdults(Math.max(0, newAdditionalAdults - 1))} disabled={isSubmitting || newAdditionalAdults === 0}>−</button>
                      <span className="spinner-value">{newAdditionalAdults}</span>
                      <button type="button" className="spinner-btn" onClick={() => setNewAdditionalAdults(Math.min(20, newAdditionalAdults + 1))} disabled={isSubmitting}>+</button>
                    </div>
                  </div>
                  <div className="reg-card-modal-spinner-group">
                    <label>Additional Children</label>
                    <div className="spinner-controls">
                      <button type="button" className="spinner-btn" onClick={() => setNewAdditionalChildren(Math.max(0, newAdditionalChildren - 1))} disabled={isSubmitting || newAdditionalChildren === 0}>−</button>
                      <span className="spinner-value">{newAdditionalChildren}</span>
                      <button type="button" className="spinner-btn" onClick={() => setNewAdditionalChildren(Math.min(20, newAdditionalChildren + 1))} disabled={isSubmitting}>+</button>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {eventInfo?.mealOptionsEnabled &&
            newStatus === RegistrationStatus.Registered && (
              <div className="reg-card-modal-meals">
                <h4>Meal Preference</h4>
                <div
                  className="reg-card-modal-meal-btns"
                  role="radiogroup"
                  aria-label="Meal preference"
                >
                  {mealOptions.map((option) => {
                    const selected = newMealPreference === option.value;
                    return (
                      <Button
                        key={option.value}
                        type="button"
                        variant={selected ? "primary" : "outline"}
                        className={`reg-card-modal-meal-btn${option.value === "NoMeal" ? " reg-card-modal-meal-btn--full" : ""} ${selected ? "selected" : ""}`}
                        onClick={() => setNewMealPreference(option.value)}
                        disabled={isSubmitting}
                        role="radio"
                        aria-checked={selected}
                      >
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

          <div className="reg-card-modal-actions">
            <div className="reg-card-modal-actions-left">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowChangeModal(false)}
                disabled={isSubmitting}
              >
                Close
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleChangeSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </BaseModal>
    </>
  );
};

export default MyRegistrationCard;
